import { useMemo, useState } from "react";
import { Card, Input, Progress, Select } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ACT_FLOW, PRIORITIES, type ActionDto } from "@audit-plan/shared";
import { download } from "../api/client";
import { useActions } from "../api/actions";
import { useSuppliersList } from "../api/audits";
import { usePeople } from "../api/people";
import { useAuth } from "../auth/AuthContext";
import { palette } from "../theme";
import { ActionStatusPill, DueLabel, PriorityPill } from "../components/pills";
import { PageHead } from "../components/audit/PageHead";
import { ActionDrawer } from "../components/action/ActionDrawer";
import { FilterBar, PageActions } from "../components/FilterBar";
import { ResponsiveList } from "../components/ResponsiveList";
import { Sensitive } from "../components/Sensitive";

export default function ActionsPage() {
  const { t } = useTranslation();
  const { me } = useAuth();
  const isSup = me?.role === "supplier";
  const people = usePeople();
  const [sp, setSp] = useSearchParams();
  const openId = sp.get("id");
  const [status, setStatus] = useState<string>();
  const [supplierId, setSupplierId] = useState<string>();
  const [priority, setPriority] = useState<string>();
  const [factoryId, setFactoryId] = useState<string>();
  const [q, setQ] = useState("");
  const suppliers = useSuppliersList();
  const overdue = status === "overdue";
  const list = useActions({ status: overdue ? undefined : status, overdue: overdue || undefined, supplierId });
  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (list.data ?? [])
      .filter((a) => (!priority || a.priority === priority) && (!factoryId || a.factoryId === factoryId) && (!term || `${a.code} ${a.title} ${a.supplierName} ${a.factoryName} ${a.ncCode}`.toLowerCase().includes(term)))
      .sort((a, b) => Number(a.status === "closed") - Number(b.status === "closed") || (a.dueDate > b.dueDate ? 1 : -1));
  }, [list.data, priority, factoryId, q]);
  const factoryOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of list.data ?? []) m.set(a.factoryId, a.factoryName);
    return [...m].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [list.data]);
  const setOpen = (id: string | null) => {
    const next = new URLSearchParams(sp);
    if (id) next.set("id", id);
    else next.delete("id");
    setSp(next, { replace: true });
  };

  return (
    <div>
      <PageHead
        title={isSup ? t("myActions") : t("actions")}
        extra={<PageActions items={[{ key: "xlsx", label: t("exportXlsx"), icon: <DownloadOutlined />, onClick: () => void download("/exports/actions.xlsx") }]} />}
      />
      <FilterBar
        active={[status, supplierId, factoryId, priority].filter(Boolean).length}
        onReset={() => { setStatus(undefined); setSupplierId(undefined); setFactoryId(undefined); setPriority(undefined); setQ(""); }}
        search={<Input.Search allowClear placeholder={t("search")} style={{ width: 260, maxWidth: "100%" }} onSearch={setQ} onChange={(e) => setQ(e.target.value)} />}
      >
        <Select allowClear placeholder={t("status")} style={{ width: 190 }} value={status} onChange={setStatus} options={[...ACT_FLOW.map((s) => ({ value: s, label: t(`as_${s}`) })), { value: "overdue", label: t("b_overdue_filter") }]} />
        {isSup ? null : <Select allowClear showSearch optionFilterProp="label" placeholder={t("supplier")} style={{ width: 220 }} value={supplierId} onChange={setSupplierId} options={(suppliers.data ?? []).map((s) => ({ value: s.id, label: s.name }))} />}
        <Select allowClear showSearch optionFilterProp="label" placeholder={t("nc_factoryFilter")} style={{ width: 240 }} value={factoryId} onChange={setFactoryId} options={factoryOptions} />
        <Select allowClear placeholder={t("priority")} style={{ width: 140 }} value={priority} onChange={setPriority} options={PRIORITIES.map((p) => ({ value: p, label: t(`pr_${p}`) }))} />
      </FilterBar>
      <Card styles={{ body: { padding: 0 } }}>
        <ResponsiveList<ActionDto>
          rowKey="id"
          loading={list.isLoading}
          dataSource={rows}
          pageSize={10}
          pageSizeKey="actions"
          emptyText={t("noResults")}
          onRowClick={(a) => setOpen(a.id)}
          card={{
            code: (a) => `${a.code} · NC ${a.ncCode}`,
            title: (a) => a.title,
            subtitle: (a) => (isSup || a.factoryName === a.supplierName ? a.factoryName : `${a.factoryName} · ${a.supplierName}`),
            tags: (a) => (
              <>
                <ActionStatusPill a={a} />
                <PriorityPill p={a.priority} />
                <DueLabel a={a} />
              </>
            ),
            fields: (a) => [
              [t("assignee"), <Sensitive>{a.assigneeId ? people.name(a.assigneeId) : a.assigneeName || "—"}</Sensitive>],
              [t("progress"), <Progress key="p" percent={a.progress} size="small" strokeColor={palette.ok} />],
            ],
          }}
          columns={[
            { title: t("code"), dataIndex: "code", render: (c: string) => <span style={{ fontFamily: "var(--ap-mono)" }}>{c}</span> },
            { title: t("nc_factory"), dataIndex: "factoryName", render: (_: unknown, a) => <div style={{ minWidth: 160 }}>{a.factoryName}{!isSup && a.supplierName !== a.factoryName ? <div style={{ fontSize: 12, color: palette.muted }}>{a.supplierName}</div> : null}</div> },
            { title: t("title"), dataIndex: "title", render: (v: string) => <div style={{ maxWidth: 420 }}>{v}</div> },
            { title: "NC", dataIndex: "ncCode", render: (c: string) => <span style={{ fontFamily: "var(--ap-mono)", fontSize: 12 }}>{c}</span> },
            { title: t("assignee"), render: (_: unknown, a) => <Sensitive>{a.assigneeId ? people.name(a.assigneeId) : a.assigneeName || "—"}</Sensitive> },
            { title: t("priority"), render: (_: unknown, a) => <PriorityPill p={a.priority} /> },
            { title: t("dueDate"), render: (_: unknown, a) => <DueLabel a={a} /> },
            { title: t("progress"), render: (_: unknown, a) => <Progress percent={a.progress} size="small" style={{ width: 90 }} strokeColor={palette.ok} /> },
            { title: t("status"), render: (_: unknown, a) => <ActionStatusPill a={a} /> },
          ]}
        />
      </Card>
      <ActionDrawer actionId={openId} onClose={() => setOpen(null)} />
    </div>
  );
}
